/**
 * ClaimGateModal Component
 * 
 * Modal shown when a user's claim is blocked due to unworked leads.
 * Displays the list of leads that need activity and provides links
 * to their deal pages.
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Progress,
  Icon,
  Alert,
  AlertIcon,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { 
  FiAlertTriangle, 
  FiPhone, 
  FiMail, 
  FiMessageSquare,
  FiExternalLink,
  FiClock,
  FiArrowRight,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export function ClaimGateModal({ 
  isOpen, 
  onClose, 
  blockedBy = [], 
  stats = {},
  message = '',
}) {
  const history = useHistory();
  
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const { maxAllowed = 5 } = stats;
  const blockedCount = blockedBy.length;
  const progressValue = (blockedCount / maxAllowed) * 100;

  const handleGoToDeal = (dealId) => {
    onClose();
    history.push(`/crm/deals/${dealId}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Icon as={FiAlertTriangle} color="orange.500" boxSize={6} />
            <Text>Claiming Blocked</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Explanation Alert */}
            <Alert status="warning" borderRadius="md" variant="left-accent">
              <AlertIcon />
              <Box>
                <Text fontWeight="medium">You have unworked leads</Text>
                <Text fontSize="sm" color="gray.600">
                  {message || `Log activity on your existing leads to unlock new claims.`}
                </Text>
              </Box>
            </Alert>

            {/* Progress Bar */}
            <Box>
              <Flex justify="space-between" mb={2}>
                <Text fontSize="sm" fontWeight="medium">
                  Unworked Leads
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {blockedCount} / {maxAllowed} max
                </Text>
              </Flex>
              <Progress 
                value={progressValue} 
                colorScheme="red" 
                size="md" 
                borderRadius="full"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Work {blockedCount} more lead{blockedCount !== 1 ? 's' : ''} to unlock claiming
              </Text>
            </Box>

            <Divider />

            {/* Required Actions */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>
                Log one of these activities on each lead:
              </Text>
              <HStack spacing={4} justify="center" wrap="wrap">
                <HStack spacing={2} color="green.600">
                  <Icon as={FiPhone} />
                  <Text fontSize="sm">Call</Text>
                </HStack>
                <HStack spacing={2} color="blue.600">
                  <Icon as={FiMessageSquare} />
                  <Text fontSize="sm">SMS</Text>
                </HStack>
                <HStack spacing={2} color="purple.600">
                  <Icon as={FiMail} />
                  <Text fontSize="sm">Email</Text>
                </HStack>
                <HStack spacing={2} color="gray.600">
                  <Icon as={FiMessageSquare} />
                  <Text fontSize="sm">Note</Text>
                </HStack>
              </HStack>
            </Box>

            <Divider />

            {/* Leads List */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>
                Leads Needing Activity:
              </Text>
              <VStack spacing={2} align="stretch" maxH="250px" overflowY="auto">
                {blockedBy.map((lead, index) => (
                  <Box
                    key={lead.queueId || index}
                    p={3}
                    bg={cardBg}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <Flex justify="space-between" align="center">
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2}>
                          <Text fontWeight="medium" fontSize="sm">
                            {lead.borrowerName || 'Unknown Borrower'}
                          </Text>
                          {lead.dealScore && (
                            <Badge colorScheme="blue" fontSize="xs">
                              Score: {lead.dealScore}
                            </Badge>
                          )}
                        </HStack>
                        <HStack spacing={2} fontSize="xs" color="gray.500">
                          <Icon as={FiClock} />
                          <Text>
                            Claimed {lead.claimedAt 
                              ? formatDistanceToNow(new Date(lead.claimedAt), { addSuffix: true })
                              : 'recently'}
                          </Text>
                        </HStack>
                      </VStack>
                      
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        rightIcon={<FiExternalLink />}
                        onClick={() => handleGoToDeal(lead.dealId)}
                        isDisabled={!lead.dealId}
                      >
                        Open
                      </Button>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
          {blockedBy.length > 0 && blockedBy[0]?.dealId && (
            <Button 
              colorScheme="blue" 
              rightIcon={<FiArrowRight />}
              onClick={() => handleGoToDeal(blockedBy[0].dealId)}
            >
              Work First Lead
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ClaimGateModal;
